import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Menu, X, Heart, ChevronRight, Users, Shield, Star, 
  Check, Crown, Mail,  
  Facebook,  Instagram, Youtube, Send,
  ArrowUp, Search, Plus, UserPlus, Eye
} from 'lucide-react';
import './HomePage.css';
import logoImage from './upscalemedia-transformed.jpeg';
import heroImage from './main image.jpg';


// Button Component
const Button = ({ children, className = '', variant = 'default', size = 'default', ...props }) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50';
  
  const variants = {
    default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
    destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
    outline: 'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
    secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    link: 'text-primary underline-offset-4 hover:underline'
  };
  
  const sizes = {
    default: 'h-9 px-4 py-2',
    sm: 'h-8 rounded-md px-3 text-xs',
    lg: 'h-10 rounded-md px-8',
    icon: 'h-9 w-9'
  };
  
  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// Input Component
const Input = ({ className = '', type = 'text', ...props }) => {
  return (
    <input
      type={type}
      className={`flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...props}
    />
  );
};

// Textarea Component
const Textarea = ({ className = '', ...props }) => {
  return (
    <textarea
      className={`flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...props}
    />
  );
};

// Select Component
const Select = ({ children, onValueChange, defaultValue, ...props }) => {
  return (
    <select
      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      onChange={(e) => onValueChange && onValueChange(e.target.value)}
      defaultValue={defaultValue}
      {...props}
    >
      {children}
    </select>
  );
};

// Preloader Component
const Preloader = () => {
  return (
    <div className="preloader">
      <div className="text-center">
        <div className="spinner mx-auto mb-4"></div>
        <h2 className="text-2xl font-bold text-white mb-2">Divine Covenant</h2>
        <p className="text-white/70">Preparing your spiritual journey...</p>
      </div>
    </div>
  );
};
// Header Component
const Header = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      
      
      const sections = ['hero', 'about', 'mission', 'features', 'pricing', 'contact'];
      const currentSection = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      setActiveSection(currentSection || '');
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-300 shadow-md border-b border-gray-200`}
      style={{ background: 'white' }}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-24">
          <div className="flex items-center gap-3">
            <img src={logoImage} alt="Divine Covenant" className="h-24 w-auto object-contain" />
          </div>

          <nav className="hidden md:flex items-center gap-8">
            {[
              { id: 'about', label: 'About' },
              { id: 'mission', label: 'Mission' },
              { id: 'features', label: 'Features' },
              { id: 'pricing', label: 'Pricing' },
              { id: 'contact', label: 'Contact' }
            ].map((item) => (
              <button
  key={item.id}
  onClick={() => scrollToSection(item.id)}
  className={`btn-ghost-divine relative hover:text-gray-900 text-lg ${
    activeSection === item.id ? 'font-semibold' : ''
  }`}
  style={{ 
    background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(43, 74%, 52%) 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  }}
>
                {item.label}
                {activeSection === item.id && (
                  <div className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-gold rounded-full" />
                )}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Button 
              variant="outline" 
              className="border-2 border-yellow-500 text-gray-900 bg-white hover:bg-yellow-50 h-10 px-6 text-base" 
              onClick={() => navigate('/login')}
            >
              Sign In
            </Button>
            <Button 
              className="border-2 border-yellow-500 bg-white text-gray-900 hover:bg-yellow-50 h-10 px-6 text-base" 
              onClick={() => navigate('/register')}
            >
              Register
            </Button>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 hover:text-gray-900 transition-colors"
            style={{ color: '#D4A747' }}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full border-t border-gray-200" style={{ background: 'white' }}>
            <div className="p-4 space-y-4">
              {[
                { id: 'about', label: 'About' },
                { id: 'mission', label: 'Mission' },
                { id: 'features', label: 'Features' },
                { id: 'pricing', label: 'Pricing' },
                { id: 'contact', label: 'Contact' }
              ].map((item) => (
                <button
  key={item.id}
  onClick={() => scrollToSection(item.id)}
  className="block w-full text-left py-2 hover:text-gray-900 transition-colors"
  style={{ 
    background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(43, 74%, 52%) 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  }}
>
                  {item.label}
                </button>
              ))}
              <div className="pt-4 border-t border-gray-200 space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full border-2 border-yellow-500 text-gray-900 bg-white hover:bg-yellow-50 h-10 px-6 text-base" 
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </Button>
                <Button 
                  className="w-full border-2 border-yellow-500 bg-white text-gray-900 hover:bg-yellow-50 h-10 px-6 text-base" 
                  onClick={() => navigate('/register')}
                >
                  Register
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};// Hero Section Component (UPDATED - removed outline)
const HeroSection = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section id="hero" className="relative min-h-screen flex items-center bg-gradient-hero overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-20 h-20 bg-accent/10 rounded-full animate-float"></div>
        <div className="absolute top-40 right-20 w-16 h-16 bg-accent/20 rounded-full animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-40 left-20 w-12 h-12 bg-accent/15 rounded-full animate-float" style={{ animationDelay: '4s' }}></div>
        <div className="absolute bottom-20 right-10 w-24 h-24 bg-accent/10 rounded-full animate-float" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className={`space-y-8 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full text-accent font-medium">
                <Heart className="w-4 h-4" />
                Newly Launched Platform
              </div>
              
              <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight">
                Find Your{' '}
                <br />
                <span className="text-gradient text-shimmer">Divine Match</span>
              </h1>
              
              <p className="text-xl text-white/80 leading-relaxed max-w-lg">
                Welcome to Divine Covenant - where Christian singles begin their sacred journey to find their God-given life partner through faith, prayer, and divine guidance.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button className="btn-divine text-lg px-8 py-4 h-auto" onClick={() => navigate('/register')}>
                Start Your Divine Journey
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>

          <div className={`relative ${isVisible ? 'animate-slide-in-right' : 'opacity-0'}`} style={{ animationDelay: '300ms' }}>
            <div className="relative">
              <div className="relative rounded-3xl p-8 transform hover:scale-105 transition-transform duration-500" style={{ background: 'transparent' }}>
                <div className="w-full h-[600px] rounded-2xl overflow-hidden">
                  <img src={heroImage} alt="Christian Couple" className="w-full h-full object-cover" />
                </div>
                
                <div className="absolute -bottom-6 -left-6 glass-card rounded-2xl p-4 max-w-xs">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-accent text-accent" />
                      ))}
                    </div>
                    <span className="text-sm font-semibold">5.0</span>
                  </div>
                  <p className="text-sm text-muted-foreground">"Found my soulmate through Divine Covenant. God's timing is perfect!"</p>
                  <p className="text-xs text-accent font-medium mt-2">- Sarah & Michael</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section Component
const AboutSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const element = document.getElementById('about');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const values = [
    {
      icon: <Plus className="w-8 h-8" />,
      title: "Faith-Centered",
      description: "Built on Christian values and biblical principles for meaningful relationships."
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Authentic Connections",
      description: "Fostering genuine relationships based on shared beliefs and values."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Community Focused",
      description: "Join a supportive community of like-minded Christian singles."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Safe & Secure",
      description: "Your privacy and safety are our top priorities with verified profiles."
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-background to-muted/30">
      <div className="container mx-auto px-4">
        <div className={`text-center mb-16 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-6">
            Where Faith Meets Love
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Divine Covenant is more than a dating platform—it's a sacred space where Christian singles can find their God-ordained life partner through prayer, faith, and divine guidance.
          </p>
        </div>

        <div className={`grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto ${isVisible ? 'animate-slide-in-right' : 'opacity-0'}`}>
          {values.map((value, index) => (
            <div 
              key={index} 
              className="glass-card rounded-2xl p-6 text-center hover-lift"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              <div className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-4 text-accent">
                {value.icon}
              </div>
              <h3 className="text-xl font-bold text-primary mb-3">{value.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{value.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};// Mission Section Component  
const MissionSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const missions = [
    {
      title: "Community of Faith",
      description: "Join thousands of Christian singles who have chosen to seek their life partner through prayer, faith, and divine guidance in our supportive community.",
      icon: <Users className="w-16 h-16" />,
      tag: "Faithful Community"
    },
    {
      title: "Guided by Prayer",
      description: "Every connection begins with prayer. We believe in God's perfect timing and guidance in bringing together hearts meant to unite in holy matrimony.",
      icon: <Heart className="w-16 h-16" />,
      tag: "Divine Guidance"
    },
    {
      title: "Built on Values",
      description: "Our platform is founded on biblical principles, ensuring that every relationship starts with a strong spiritual foundation and shared Christian values.",
      icon: <Shield className="w-16 h-16" />,
      tag: "Biblical Foundation"
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const element = document.getElementById('mission');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

 useEffect(() => {
  const timer = setInterval(() => {
    setCurrentSlide((prev) => (prev + 1) % missions.length);
  }, 5000);

  return () => clearInterval(timer);
}, [missions.length]); 

  return (
    <section id="mission" className="py-20 bg-gradient-to-br from-muted/30 to-background">
      <div className="container mx-auto px-4">
        <div className={`text-center mb-16 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-6">
            Our Mission
          </h2>
          <div className="w-20 h-1 bg-gradient-gold mx-auto mb-6"></div>
        </div>

        <div className={`relative max-w-5xl mx-auto ${isVisible ? 'animate-slide-in-right' : 'opacity-0'}`}>
          <div className="glass-card rounded-3xl p-8 md:p-12 text-center min-h-[450px] flex flex-col justify-center">
            <div className="text-accent mb-6 flex justify-center">
              {missions[currentSlide].icon}
            </div>
            <h3 className="text-3xl font-bold text-primary mb-6">
              {missions[currentSlide].title}
            </h3>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed max-w-3xl mx-auto">
              {missions[currentSlide].description}
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full text-accent font-medium mx-auto">
              <Heart className="w-4 h-4" />
              {missions[currentSlide].tag}
            </div>
          </div>

          <div className="flex justify-center mt-8 gap-3">
            {missions.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-3 rounded-full transition-all duration-300 ${
                  index === currentSlide ? 'bg-accent w-8' : 'bg-muted w-3'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Features Section Component
const FeaturesSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentFeature, setCurrentFeature] = useState(1);

  const features = [
    {
      title: "Create Your Personalized Account",
description: "Easily create and manage your account, ensuring a smooth and tailored experience for your matchmaking journey.",
icon: <UserPlus className="w-8 h-8" />,
detail: "Seamless account management"
    },
    {
      title: "Showcase Your Profile",
description: "Upload clear and engaging profile pictures to share your personality and values with others.",
icon: <Users className="w-8 h-8" />,
detail: "Picture upload feature"
    },
    {
      title: "Express Interest",
description: "Send your interests to potential matches to begin meaningful conversations and explore compatibility.",
icon: <Heart className="w-8 h-8" />,
detail: "Interest notification system"
    },
    {
      title: "Advanced Search Filters",
description: "Utilize our advanced search options to find profiles that align with your faith, lifestyle, and personal preferences.",
icon: <Search className="w-8 h-8" />,
detail: "Faith-based filtering"
    },
    {
      title: "View Comprehensive Profiles",
description: "Shortlist potential matches and get a deeper look at their profiles, including details that matter to you.",
icon: <Eye className="w-8 h-8" />,
detail: "Detailed profile insights"
    },
    {
      title: "Explore Unlimited Profiles",
description: "Browse through an extensive range of profiles without any limitations, increasing your chances of finding the right match.",
icon: <Users className="w-8 h-8" />,
detail: "Unlimited browsing access"
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const element = document.getElementById('features');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

 useEffect(() => {
  const timer = setInterval(() => {
    setCurrentFeature((prev) => (prev + 1) % 6); // Hardcode the length
  }, 5000);

  return () => clearInterval(timer);
}, []);

  return (
    <section id="features" className="py-20 bg-gradient-to-br from-background to-muted/30">
      <div className="container mx-auto px-4">
        <div className={`text-center mb-16 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-6">
            Features That Bless Your Journey
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Discover powerful features designed to help Christian singles find their God-ordained life partner with faith, safety, and community support.
          </p>
        </div>

        <div className={`grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto ${isVisible ? 'animate-slide-in-right' : 'opacity-0'}`}>
          {features.map((feature, index) => (
            <div
              key={index}
              className={`glass-card rounded-2xl p-6 cursor-pointer transition-all duration-300 ${
                index === currentFeature
                  ? 'scale-105 shadow-divine border-2 border-accent/30 bg-accent/5'
                  : 'hover:scale-102'
              }`}
              onClick={() => setCurrentFeature(index)}
            >
              <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-4 ${
                index === currentFeature ? 'bg-gradient-gold text-white' : 'bg-muted text-muted-foreground'
              }`}>
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">{feature.title}</h3>
              <p className="text-muted-foreground mb-3">{feature.description}</p>
              <div className="flex items-center gap-2 text-sm text-accent">
                <Star className="w-4 h-4" />
                <span>{feature.detail}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-center mt-12 gap-2">
          {features.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentFeature(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentFeature ? 'bg-accent w-8' : 'bg-muted w-2'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};// Pricing Section
const PricingSection = () => {
  const navigate = useNavigate();
  const [isYearly, setIsYearly] = useState(false);

  const plans = [
    {
      name: "Free",
      monthlyPrice: 0,
      yearlyPrice: 0,
      features: [
        "Create and manage a personal account",
     "Upload and showcase profile images",
     "Send interests to other members",
     "Access basic search and match filters",
     "View limited profile details",
     "Discover up to 5 profiles daily"
      ],
      popular: false
    },
    {
      name: "Premium",
      monthlyPrice: 50,
      yearlyPrice: 600,
      features: [
        "Create and manage a personal account",
     "Upload and showcase profile images",
     "Send interests to other members",
     "Access advanced search and match filters",
     "Shortlist and view complete profile details",
     "Discover unlimited profiles without restrictions"
      ],
      popular: true
    }
  ];

  return (
    <section id="pricing" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Choose Your Path to Love
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Start your journey for free or unlock premium features to find your divine match faster.
          </p>

          <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
            <button
              onClick={() => setIsYearly(false)}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                !isYearly ? 'bg-white shadow-md' : 'text-gray-600'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className={`px-6 py-2 rounded-full font-medium transition-all relative ${
                isYearly ? 'bg-white shadow-md' : 'text-gray-600'
              }`}
            >
              Yearly
              
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan, index) => {
            const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
            

            return (
              <div
                key={index}
                className={`relative bg-white rounded-3xl p-8 transition-all duration-300 ${
                  plan.popular
                    ? 'shadow-2xl scale-105 ring-2 ring-yellow-400'
                    : 'shadow-lg hover:shadow-xl'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="text-center mb-8">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                    plan.popular 
                      ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' 
                      : 'bg-gray-100'
                  }`}>
                    {plan.popular ? (
                      <Crown className="w-8 h-8 text-white" />
                    ) : (
                      <Heart className="w-8 h-8 text-gray-600" />
                    )}
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>

                  <div className="mb-4">
                    <span className="text-4xl font-bold text-gray-900">£{price}</span>
                    {plan.name !== 'Free' && (
                      <span className="text-gray-600 ml-1">
                        /{isYearly ? 'year' : 'month'}
                      </span>
                    )}
                  </div>

                  
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
<Button 
  className="w-full bg-yellow-500 hover:bg-yellow-600 text-white"
  onClick={() => {
    console.log('Button clicked! Plan name:', plan.name);
    console.log('Navigating to:', plan.name === 'Free' ? '/register' : '/premium-upgrade');
    navigate(plan.name === 'Free' ? '/register' : '/premium-upgrade');
  }}
>
  {plan.name === 'Free' ? 'Get Started Free' : `Choose ${plan.name}`}
</Button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
 
  const [formData, setFormData] = useState({
    contactName: '',
    contactEmail: '',
    phone: '',
    subject: '',
    message: '',
    signupEmail: '',
    password: '',
    confirmPassword: ''
  });

  const handleSubmit = (e, formType) => {
    e.preventDefault();
    alert(`${formType} form submitted!`);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Start Your Divine Journey
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Have questions or ready to begin? We're here to guide you every step of the way.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Contact Form */}
          <div className="bg-white rounded-3xl p-8 shadow-xl">
            <div className="text-center mb-6">
              <Heart className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Get in Touch</h3>
              <p className="text-gray-600">We'd love to hear from you and answer any questions.</p>
            </div>

            <form onSubmit={(e) => handleSubmit(e, 'Contact')} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  name="contactName"
                  placeholder="Your Name"
                  value={formData.contactName}
                  onChange={handleChange}
                  required
                />
                <Input
                  type="email"
                  name="contactEmail"
                  placeholder="Your Email"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  type="tel"
                  name="phone"
                  placeholder="Phone (optional)"
                  value={formData.phone}
                  onChange={handleChange}
                />
                <Select name="subject" onValueChange={(value) => setFormData({...formData, subject: value})}>
                  <option value="">Subject</option>
                  <option value="general">General Inquiry</option>
                  <option value="support">Support</option>
                  <option value="partnership">Partnership</option>
                  <option value="feedback">Feedback</option>
                </Select>
              </div>

              <Textarea
                name="message"
                placeholder="Your message..."
                rows={5}
                value={formData.message}
                onChange={handleChange}
                required
              />

              <Button type="submit" className="w-full bg-yellow-500 hover:bg-yellow-600 text-white">
                Send Message
                <Send className="ml-2 w-4 h-4" />
              </Button>
            </form>
          </div>

          {/* Signup Form and Contact Info */}
          <div>
           
            {/* Contact Information */}
            <div className="bg-white rounded-3xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Email</p>
                    <p className="text-gray-600">divinecovenant.uk@gmail.com
</p>
                  </div>
                </div>

                

                
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-4">Follow us on social media</p>
                
              <div className="flex gap-3">
  {[Facebook, Instagram].map((Icon, index) => (
    <button
      key={index}
      className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center hover:bg-yellow-200 transition-colors"
    >
      <Icon className="w-5 h-5 text-yellow-600" />
    </button>
  ))}
</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};// Footer Component
const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="overflow-hidden">
                <img src={logoImage} alt="Divine Covenant" className="h-16 w-auto object-contain" />
              </div>
            </div>
            <p className="text-gray-400 mb-6 max-w-md">
              Where Christian singles find their God-ordained life partner through faith, prayer, and divine guidance.
            </p>
            <div className="flex gap-4">
  {[Facebook, Instagram, Youtube].map((Icon, index) => (
    <button
      key={index}
      className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors"
    >
      <Icon className="w-5 h-5" />
    </button>
  ))}
</div>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-gray-400">
              <li><a href="#about" className="hover:text-white transition-colors">About Us</a></li>
              <li><a href="#mission" className="hover:text-white transition-colors">Our Mission</a></li>
              <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
              <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
              <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Support</h4>
            <ul className="space-y-2 text-gray-400">
              <li><a href="/help" className="hover:text-white transition-colors">Help Center</a></li>
<li><a href="/safety" className="hover:text-white transition-colors">Safety Guidelines</a></li>
              <li><a href="/community-guidelines" className="hover:text-white transition-colors">Community Guidelines</a></li>
<li><a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a></li>
<li><a href="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © 2024 Divine Covenant. All rights reserved. Built with faith and love.
            </p>
            <div className="flex gap-6 text-gray-400 text-sm">
              <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
<a href="/terms" className="hover:text-white transition-colors">Terms</a>
<a href="/cookies" className="hover:text-white transition-colors">Cookies</a>
              <span className="flex items-center gap-1">
                <Heart className="w-3 h-3 text-yellow-500" />
                Made with love
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Back to Top Button
const BackToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <button
      onClick={scrollToTop}
      className={`fixed bottom-8 right-8 w-12 h-12 bg-yellow-500 text-white rounded-full shadow-lg hover:bg-yellow-600 hover:scale-110 transition-all duration-300 z-50 flex items-center justify-center ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'
      }`}
    >
      <ArrowUp className="w-6 h-6" />
    </button>
  );
};

// Main App Component
const DivineCovenantLanding = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) return <Preloader />;

  return (
    <div className="divine-covenant-landing">
      <Header />
      <main>
        <HeroSection />
        <AboutSection />
        <MissionSection />
        <FeaturesSection />
        <PricingSection />
        <ContactSection />
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default DivineCovenantLanding;